import { useTranslation } from 'react-i18next'

import type { BalanceKomaRow } from '~/components/balance/KomaTable'
import {
  KomaTable as BalanceKomaTable,
  DEVIATION_THRESHOLD_PCT,
} from '~/components/balance/KomaTable'
import type { DetailKomaData } from '~/pages/BalanceDemandDetail/models/generateMockData'

type Props = {
  data: DetailKomaData[]
  /** 現在コマ (0 = 該当なし)。 当日表示中だけ行をハイライトする (`doji-bg.md:382`)。 */
  currentKoma: number
}

// BalanceDemandDetail 固有の行 shape (demandPlan/demandActual/actualStatus) を
// 共通 `~/components/balance/KomaTable` の row 型にマッピングする薄い wrapper。
//
// 計画値の有無で表示を落とす分岐 (旧 planAvailable) は、電圧階級フィルターの撤去
// (issue #4734) で削除した。計画なし (帰属不明・受領済み計画の無い日など) でも
// 列ごと落とす分岐は戻さず、demandPlan = null を共通 KomaTable の「—」で出す
// (列構成を選択や日付で変えない)。
export const KomaTable = ({ data, currentKoma }: Props): React.ReactElement => {
  const { t } = useTranslation()

  const rows: BalanceKomaRow[] = data.map((d) => ({
    koma: d.koma,
    time: d.time,
    plan: d.demandPlan,
    actual: d.demandActual,
    actualStatus:
      d.actualStatus === 'missing' || d.actualStatus === 'interpolated'
        ? d.actualStatus
        : undefined,
    awaitingVoltageClasses: d.awaitingVoltageClasses,
  }))

  return (
    <BalanceKomaTable
      currentKoma={currentKoma}
      data={rows}
      // 左ペインを 380px にしたので 4 列が収まる。 横スクロールを止めて最終列
      // (誤差) に右余白を足す。 design はこの対策を `.region-left`
      // (= 需要詳細の 380px ペイン) にだけスコープしており、 280px のままの
      // 発電詳細に当てると最終列が切り取られる (`css/balance.css:300,424`)。
      fitsWithoutHorizontalScroll
      // 誤差(%) の説明は **需要詳細だけ**に置く。 design
      // `balance/bg-region.html:158` が `data-tooltip` を持つのはこの画面で、
      // 発電側の 2 画面は素の `<th>誤差 (%)</th>`。
      //
      // 閾値は `DEVIATION_THRESHOLD_PCT` を差し込む。 文言に 1.5 を直書きすると
      // 閾値を変えた時に説明だけが古いまま残る (しかも ja / en の 2 言語ぶん)。
      headerHints={{
        error: {
          content: t('page:balanceDemandDetail.table.errorHint', {
            threshold: DEVIATION_THRESHOLD_PCT,
          }),
          ariaLabel: t('page:balanceDemandDetail.table.errorHintAriaLabel'),
        },
      }}
      labelKeyPrefix="page:balanceDemandDetail.table"
    />
  )
}
